import type { EngagementCheck, MissingValue } from '@kanos/domain';

/** The steps of the guided contract editor, in their order. */
export const ENGAGEMENT_STEPS = [
  'client',
  'services',
  'cooperation',
  'communication',
  'offer',
  'review',
] as const;
export type EngagementStep = (typeof ENGAGEMENT_STEPS)[number];

/** Where a gap is closed: the firm's data, its catalogue, the client's master data, this contract. */
export type FixPlace = 'firm' | 'catalog' | 'client' | 'contract';

export interface ReadinessItem {
  /**
   * The place and topic of the gap, stable across checks: the list's keys, and what merges the
   * same gap named twice - one point per topic, never one per missing value.
   */
  key: string;
  place: FixPlace;
  /** The topic in a few words („Preise“, „Haftungshöchstbetrag“). */
  topic: string;
  /** A plain sentence for the firm - no rule id, no expression. */
  text: string;
  /** A short legal reference, shown muted. */
  reference?: string;
  /** In this contract: the step that closes it. */
  step?: EngagementStep;
  /** In the firm's data: the area that holds it (a slug of the navigation). */
  area?: string;
  /** What the point gathers, each in its own sentence - the firm's data lacking several things. */
  parts?: readonly ReadinessItem[];
}

export interface Readiness {
  /** What keeps the offer back, one point per place and topic. */
  blocking: readonly ReadinessItem[];
  /** What changes nothing now - before the acceptance, or worth knowing. */
  notes: readonly ReadinessItem[];
  /**
   * What the server could not check, in its plain words - a technical failure is one line, its
   * details are in the log, never on the page.
   */
  problems: readonly string[];
}

const COMMUNICATION: ReadinessItem = {
  key: 'contract:communication',
  place: 'contract',
  step: 'communication',
  topic: 'E-Mail-Schutz und Post',
  text: 'Wählen Sie, wie E-Mails geschützt werden und ob Post verschickt werden darf.',
};
const FIRM_DETAILS: ReadinessItem = {
  key: 'firm:details',
  place: 'firm',
  area: 'details',
  topic: 'Pflichtangaben',
  text: 'Vervollständigen Sie die Pflichtangaben Ihrer Kanzlei: Name, Anschrift, Kontakt, Vertretung, Kammer und Berufshaftpflicht.',
  reference: 'DL-InfoV',
};
const DISPUTE: ReadinessItem = {
  key: 'firm:dispute',
  place: 'firm',
  area: 'base-data',
  topic: 'Verbraucherschlichtung',
  text: 'Geben Sie an, ob Ihre Kanzlei an Verfahren vor einer Verbraucherschlichtungsstelle teilnimmt.',
  reference: '§ 36 VSBG',
};
const DPO: ReadinessItem = {
  key: 'firm:dpo',
  place: 'firm',
  area: 'base-data',
  topic: 'Datenschutzbeauftragter',
  text: 'Geben Sie Ihren Datenschutzbeauftragten an – oder dass Sie keinen benennen müssen.',
  reference: 'Art. 13 DSGVO',
};
const CREDITOR: ReadinessItem = {
  key: 'firm:creditor',
  place: 'firm',
  area: 'base-data',
  topic: 'Gläubiger-ID',
  text: 'Hinterlegen Sie die Gläubiger-Identifikationsnummer Ihrer Kanzlei – ohne sie keine Lastschrift.',
};
const FIRM_LEGAL_FORM: ReadinessItem = {
  key: 'firm:legal-form',
  place: 'firm',
  area: 'base-data',
  topic: 'Rechtsform der Kanzlei',
  text: 'Tragen Sie die Rechtsform Ihrer Kanzlei ein.',
};

/** The tasks of the rule pack `stb-vertrag`, in plain words and with the place to fix them. */
const TASKS: Readonly<Record<string, ReadinessItem>> = {
  sperre_kanzleiangaben: FIRM_DETAILS,
  sperre_streitbeilegung: DISPUTE,
  sperre_datenschutzbeauftragter: DPO,
  sperre_glaeubiger_id: CREDITOR,
  sperre_haftung: {
    key: 'contract:liability',
    place: 'contract',
    step: 'offer',
    topic: 'Haftungshöchstbetrag',
    text: 'Legen Sie den Haftungshöchstbetrag fest – passend zu Ihrer Berufshaftpflicht.',
    reference: '§ 67a StBerG',
  },
  sperre_firmenlastschrift: {
    key: 'contract:direct-debit',
    place: 'contract',
    step: 'cooperation',
    topic: 'Art der Lastschrift',
    text: 'Für Privatpersonen ist nur die SEPA-Basislastschrift möglich.',
  },
  sperre_leistung: {
    key: 'contract:service',
    place: 'contract',
    step: 'services',
    topic: 'Mindestens eine Leistung',
    text: 'Wählen Sie mindestens eine Leistung.',
  },
  sperre_fibu_angaben: {
    key: 'contract:bookkeeping',
    place: 'contract',
    step: 'services',
    topic: 'Angaben zur Finanzbuchhaltung',
    text: 'Legen Sie für die Finanzbuchhaltung Modell, Buchungszeitraum und Beginn fest.',
  },
  sperre_fibu_paket: {
    key: 'contract:bookkeeping-package',
    place: 'contract',
    step: 'services',
    topic: 'Paket der Finanzbuchhaltung',
    text: 'Wählen Sie ein Paket für die Finanzbuchhaltung.',
  },
  sperre_private_steuern: {
    key: 'contract:private-taxes',
    place: 'contract',
    step: 'services',
    topic: 'Private Steuern',
    text: 'Private Steuern lassen sich noch nicht anbieten – die Bausteine dafür fehlen noch.',
  },
  sperre_kommunikation: COMMUNICATION,
  sperre_angebotsbindung: {
    key: 'contract:offer-validity',
    place: 'contract',
    step: 'offer',
    topic: 'Gültigkeit des Angebots',
    text: 'Legen Sie fest, bis wann das Angebot gilt – nicht in der Vergangenheit.',
  },
  annahme_identifizierung: {
    key: 'client:identification',
    place: 'client',
    topic: 'Identifizierung',
    text: 'Vor der Annahme: Identifizieren Sie die Vertragspartei.',
    reference: '§ 11 GwG',
  },
  annahme_wirtschaftlich_berechtigte: {
    key: 'client:beneficial-owners',
    place: 'client',
    topic: 'Wirtschaftlich Berechtigte',
    text: 'Vor der Annahme: Erfassen Sie die wirtschaftlich Berechtigten.',
    reference: '§ 10 GwG',
  },
  hinweis_verguetungsvereinbarung: {
    key: 'contract:fee-agreement',
    place: 'contract',
    topic: 'Vergütungsvereinbarung',
    text: 'Ein Preis weicht von der StBVV ab – die Vergütungsvereinbarung liegt deshalb als eigenes Dokument bei.',
    reference: '§ 4 StBVV',
  },
  hinweis_verbraucher_klauseln: {
    key: 'contract:consumer-clauses',
    place: 'contract',
    topic: 'Klauseln für Privatpersonen',
    text: 'Die Klauseln für Privatpersonen sind fachlich noch nicht abschließend geprüft.',
  },
  hinweis_beratung_allein: {
    key: 'contract:advice-alone',
    place: 'contract',
    step: 'services',
    topic: 'Begleitende Beratung',
    text: 'Begleitende Beratung ist nur neben einer laufenden Leistung sinnvoll.',
  },
};

/** Values not known yet that name a gap of their own, by the value's name. */
const VALUES: Readonly<Record<string, ReadinessItem>> = {
  email_protection: COMMUNICATION,
  post_mode: COMMUNICATION,
  firm_dispute_resolution: DISPUTE,
  'firm.dispute_resolution': DISPUTE,
  firm_has_dpo: DPO,
  'dpo.name': DPO,
  'dpo.contact': DPO,
  creditor_id: CREDITOR,
  firm_legal_form: FIRM_LEGAL_FORM,
  'firm.legal_form': FIRM_LEGAL_FORM,
  client_legal_form: {
    key: 'contract:client-legal-form',
    place: 'contract',
    step: 'client',
    topic: 'Rechtsform des Mandanten',
    text: 'Wählen Sie die Rechtsform des Mandanten.',
  },
};

/**
 * A topic that gathers values of one place: each value adds its description to one sentence -
 * a catalogue without prices is one point, not one per position.
 */
interface Topic {
  item: Omit<ReadinessItem, 'text'>;
  /** The sentence before the values, e.g. „Legen Sie in Ihrem Katalog die Preise fest“. */
  lead: string;
}

const PRICES: Topic = {
  item: { key: 'catalog:prices', place: 'catalog', topic: 'Preise' },
  lead: 'Legen Sie in Ihrem Katalog die Preise fest',
};
const CATALOG_RULES: Topic = {
  item: { key: 'catalog:rules', place: 'catalog', topic: 'Regeln und Fristen' },
  lead: 'Legen Sie in Ihrem Katalog Regeln und Fristen fest',
};
/** The catalogue's general rules and deadlines - every other value of the catalogue is a price. */
const RULE_VALUES: ReadonlySet<string> = new Set([
  'time_rounding_model',
  'expenses_model',
  'firm_accepts_paper',
  'fibu.deadline_day',
  'fibu.deadline_day_extended',
  'lohn.deadline_day',
  'lohn.completion_day',
  'ja.deadline',
  'notice_period',
  'excluded_tax_areas',
  'excluded_law_areas',
  'payment_day',
  'absence_fee.hours',
]);
const INSURANCE: Topic = {
  item: { key: 'firm:insurance', place: 'firm', area: 'base-data', topic: 'Berufshaftpflicht' },
  lead: 'Ergänzen Sie Ihre Berufshaftpflicht',
};
const CLIENT: Topic = {
  item: { key: 'client:master-data', place: 'client', topic: 'Stammdaten' },
  lead: 'In den Stammdaten des Mandanten fehlt noch',
};
const FIELDS: Topic = {
  item: { key: 'contract:fields', place: 'contract', step: 'offer', topic: 'Freie Angaben' },
  lead: 'Für die Dokumente fehlt noch',
};
const termsOf = (step: EngagementStep): Topic => ({
  item: { key: `contract:terms:${step}`, place: 'contract', step, topic: 'Angaben' },
  lead: 'Im Vertrag fehlt noch',
});

/** The step of a choice of this contract, by the name of the value it gives. */
const stepOfTerm = (name: string): EngagementStep =>
  /^(liability|offer)/.test(name)
    ? 'offer'
    : /^(email|post|sensitive|excludes|.*power|money|tax_|disclosure|third_party_experts)/.test(
          name,
        )
      ? 'communication'
      : /^(receipt|sepa|keeps|online|bank|assigns|pickup|payroll_reports|sign_off|firm_scans|permanent|uses_)/.test(
            name,
          )
        ? 'cooperation'
        : /^(client|is_|has_previous|is_founder)/.test(name)
          ? 'client'
          : 'services';

/** The gap of a value a document lacks: one of its own, or the topic it adds a line to. */
function gapOfValue(value: MissingValue): ReadinessItem | Topic {
  const known = VALUES[value.name];
  if (known) return known;
  switch (value.source) {
    case 'contract':
      return FIELDS;
    case 'terms':
      return termsOf(stepOfTerm(value.name));
    case 'catalog':
      return RULE_VALUES.has(value.name) ? CATALOG_RULES : PRICES;
    case 'client':
      return CLIENT;
    case 'firm':
      return /^(insurer|insurance|min_insurance)/.test(value.name) ? INSURANCE : FIRM_DETAILS;
  }
}

/** How many values a topic names before the rest are counted. */
const NAMED = 3;

const listOf = (values: readonly string[]) => {
  const named = values.slice(0, NAMED);
  const rest = values.length - named.length;
  return rest > 0
    ? `${named.join(', ')} und ${String(rest)} weitere`
    : named.length > 1
      ? `${named.slice(0, -1).join(', ')} und ${named.at(-1) ?? ''}`
      : named.join('');
};

/**
 * One point per key: the same gap named by a rule and by a document shows once, the values of
 * one topic as one sentence.
 */
function merge(gaps: readonly { gap: ReadinessItem | Topic; value?: string }[]): ReadinessItem[] {
  const merged = new Map<string, { gap: ReadinessItem | Topic; values: string[] }>();
  for (const { gap, value } of gaps) {
    const key = 'lead' in gap ? gap.item.key : gap.key;
    const entry = merged.get(key) ?? { gap, values: [] };
    if (value !== undefined && !entry.values.includes(value)) entry.values.push(value);
    merged.set(key, entry);
  }
  return [...merged.values()].map(({ gap, values }) =>
    'lead' in gap ? { ...gap.item, text: `${gap.lead}: ${listOf(values)}.` } : gap,
  );
}

/**
 * Everything the firm's data lacks as one point - „Kanzleidaten vervollständigen“ with what is
 * missing below it - as a catalogue without prices is one point: the firm fixes it in one place,
 * once, not thirteen times. Its way leads to the area of the first part, the details first.
 */
function gatherFirm(items: readonly ReadinessItem[]): ReadinessItem[] {
  const firm = items.filter((i) => i.place === 'firm');
  if (firm.length < 2) return [...items];
  const area = firm.find((i) => i.area === 'details')?.area ?? firm[0]?.area;
  const gathered: ReadinessItem = {
    key: 'firm:data',
    place: 'firm',
    topic: 'Kanzleidaten',
    text: 'Vervollständigen Sie Ihre Kanzleidaten:',
    parts: firm,
    ...(area === undefined ? {} : { area }),
  };
  const at = items.indexOf(firm[0] as ReadinessItem);
  const rest = items.filter((i) => i.place !== 'firm');
  return [...rest.slice(0, at), gathered, ...rest.slice(at)];
}

/**
 * What the check of a version says, for the firm: what keeps the offer back - one point per
 * place and topic, each in a plain sentence - what is only worth knowing, and whether a check
 * failed.
 */
export function readinessOf(check: EngagementCheck): Readiness {
  const tasks = check.rules.flatMap((r) => r.tasks);
  const problems = [...new Set(check.documents.flatMap((d) => d.problems))];
  const itemOfTask = (task: { key: string; title: string }): ReadinessItem =>
    TASKS[task.key] ?? {
      key: `task:${task.key}`,
      place: 'contract',
      step: 'review',
      topic: task.title,
      text: task.title,
    };
  const blocking = gatherFirm(
    merge([
      ...tasks
        .filter((t) => t.weight === 'blocking' && t.key !== 'auswertung')
        .map((t) => ({ gap: itemOfTask(t) })),
      ...check.documents
        .flatMap((d) => d.missing)
        .map((value) => ({ gap: gapOfValue(value), value: value.description })),
    ]),
  );
  const notes = merge(
    tasks.filter((t) => t.weight !== 'blocking').map((t) => ({ gap: itemOfTask(t) })),
  );
  return { blocking, notes, problems };
}

/** The steps that still hold something back. */
export const blockedSteps = (readiness: Readiness): ReadonlySet<EngagementStep> =>
  new Set(readiness.blocking.flatMap((i) => (i.place === 'contract' && i.step ? [i.step] : [])));

/** The points of the offer a step holds back - what the step says is still missing. */
export const gapsAt = (readiness: Readiness, step: EngagementStep): ReadinessItem[] =>
  readiness.blocking.filter((i) => i.place === 'contract' && i.step === step);

/** How many points of the offer a step holds back. */
export const gapsOf = (readiness: Readiness, step: EngagementStep): number =>
  gapsAt(readiness, step).length;

/** The places of the gaps beside the steps, in this order: this contract first. */
const PLACES: readonly FixPlace[] = ['contract', 'firm', 'catalog', 'client'];

const stepIndex = (item: ReadinessItem) => ENGAGEMENT_STEPS.indexOf(item.step ?? 'review');

/** A place with open points: how many, and the point whose way leads there. */
export interface OpenPlace {
  place: FixPlace;
  count: number;
  /** The way there: in this contract the earliest step with an open point, else the first. */
  item: ReadinessItem;
}

/**
 * The open points by the place they are closed - one row each, with how many - instead of one
 * line per point: the full list stays with „Prüfen & ausgeben“.
 */
export const openPlacesOf = (readiness: Readiness): OpenPlace[] =>
  PLACES.flatMap((place) => {
    const items = readiness.blocking.filter((i) => i.place === place);
    const [item] =
      place === 'contract'
        ? [...items].sort((a, b) => stepIndex(a) - stepIndex(b))
        : [items.find((i) => i.area) ?? items[0]];
    return item ? [{ place, count: items.length, item }] : [];
  });
